import {
  type AuthController,
  type DataSource,
  type Entity,
  useAuthController,
  useDataSource,
} from "@firecms/core";
import { useCallback, useEffect, useState } from "react";
import {
  buildEventsCollection,
  eventsCollection,
} from "../collections/events";
import { type Event } from "../collections/events";
import { type AuthExtras } from "../types/auth";
import { buildEventsPath } from "../collections/events";
import type { AccountCollectionId } from "../workspace/collectionIds";
import { canAdminAccount } from "../utils";
import {
  type AssignedParticipant,
  buildAssignedParticipantsCollection,
  buildAssignedParticipantsPath,
} from "../collections/participants";
import { type Team, buildTeamsCollection } from "../collections/teams";
import { type EventStats, type TeamStats } from "../types/stats";
import type { EventCollectionId } from "../workspace/collectionIds";
import { getEventPath } from "../workspace/collectionIds";
import { spaceIdFromEventEntity } from "../workspace/eventIdentity";

type DataSourceFetchers = Pick<DataSource, "fetchCollection" | "fetchEntity">;

const eventsCache = new Map<string, Entity<Event>[]>();

const filterEventsBySpace = (
  events: Entity<Event>[],
  spaceFilter?: string
): Entity<Event>[] => {
  if (!spaceFilter) {
    return events;
  }
  return events.filter(
    (event) => spaceIdFromEventEntity(event) === spaceFilter
  );
};

export const fetchEvents = async (
  dataSource: DataSourceFetchers,
  authController: AuthController,
  accountId: AccountCollectionId,
  spaceFilter?: string
) => {
  const extras: AuthExtras = authController.extra;

  if (!accountId.accountId) {
    return [];
  }

  if (!canAdminAccount(accountId, extras)) {
    return [];
  }

  const path = buildEventsPath(accountId);
  const events = await dataSource.fetchCollection<Event>({
    path,
    collection: buildEventsCollection(accountId),
  });

  return filterEventsBySpace(events, spaceFilter);
};

const getTeamStats = (
  participants: Entity<AssignedParticipant>[],
  teams: Entity<Team>[]
): TeamStats[] => {
  const teamStats: TeamStats[] = [];

  for (const team of teams) {
    const teamParticipants = participants.filter(
      (participant) => participant.values?.teamRef?.id === team.id
    );

    teamStats.push({
      participants: teamParticipants,
      teamNumber: team.values?.teamNumber ?? 0,
      team,
    });
  }

  return teamStats.sort((a, b) => a.teamNumber - b.teamNumber);
};

const getEventStats = async (
  dataSource: DataSource,
  eventId: EventCollectionId
): Promise<EventStats> => {
  const eventsPath = buildEventsPath({ accountId: eventId.accountId });
  const assignedPath = buildAssignedParticipantsPath(eventId);
  const teamsPath = `${getEventPath(eventId)}/teams`;

  const event = await dataSource.fetchEntity<Event>({
    path: eventsPath,
    collection: eventsCollection,
    entityId: eventId.eventId,
  });

  const teamSlotCount = event?.values?.teamSlotCount || 0;
  const maxMembersPerTeam = event?.values?.maxMembersPerTeam || 0;
  const participantSlotCount = event?.values?.participantSlotCount || 0;

  const availableSlots =
    participantSlotCount > 0
      ? participantSlotCount
      : maxMembersPerTeam * teamSlotCount;

  const teams = await dataSource.fetchCollection({
    path: teamsPath,
    collection: buildTeamsCollection(eventId),
  });

  const assignedParticipants = await dataSource.fetchCollection({
    path: assignedPath,
    collection: buildAssignedParticipantsCollection(eventId),
    limit: 1500,
  });

  const occupiedSlots = assignedParticipants.length;
  const totalSlots = availableSlots;

  const teamsWithMembers = teams.filter((team) =>
    assignedParticipants.some(
      (participant) => participant.values?.teamRef?.id === team.id
    )
  ).length;

  const teamStats = getTeamStats(assignedParticipants, teams);

  return {
    availableSlots,
    totalSlots,
    occupiedSlots,
    totalTeams: teams.length,
    activeTeams: teamsWithMembers,
    teamSlotCount,
    maxMembersPerTeam,
    teamStats,
  };
};

export const useEventStats = (eventId: EventCollectionId | null) => {
  const dataSource = useDataSource();
  const [stats, setStats] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!eventId) {
      setStats(null);
      return;
    }

    setLoading(true);
    try {
      const next = await getEventStats(dataSource, eventId);
      setStats(next);
    } finally {
      setLoading(false);
    }
  }, [dataSource, eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { eventStats: stats, stats, loading, refresh };
};

export const useEvents = (
  accountId?: AccountCollectionId,
  spaceFilter?: string
) => {
  const dataSource = useDataSource();
  const authController = useAuthController();
  const [events, setEvents] = useState<Entity<Event>[]>([]);
  const [loading, setLoading] = useState(Boolean(accountId?.accountId));

  const cacheKey = accountId?.accountId
    ? `${accountId.accountId}:${spaceFilter ?? ""}`
    : "";

  useEffect(() => {
    if (!accountId?.accountId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const cached = eventsCache.get(cacheKey);
        if (cached) {
          if (!cancelled) {
            setEvents(cached);
          }
          return;
        }

        const fetched = (await fetchEvents(
          dataSource,
          authController,
          accountId,
          spaceFilter
        )).filter(
          (event) =>
            event.values.status !== "archived" &&
            event.values.status !== "deleting"
        );

        eventsCache.set(cacheKey, fetched);
        if (!cancelled) {
          setEvents(fetched);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [accountId, authController, cacheKey, dataSource, spaceFilter]);

  return { events, loading };
};
